// import React from 'react'
import * as Sentry from '@sentry/node'
// eslint-disable-next-line no-unused-vars
import { Request, Response } from 'express'
import NextErrorComponent from 'next/error'
import React from 'react'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import { HubParagraph, HubPre, HubHeadline, HubImage } from 'src/common/rtl/HubWidgets'
import { RTLMainWrapper } from 'src/common/rtl/RTLWrapper'
import messages from '@Modules/hubs/main/messages'

type ErrorPageProps = {
  statusCode?: number
  hasGetInitialPropsRun?: any
  error?: any
  msg: string
  vctraceid: string
}

const ErrorPage = (props: ErrorPageProps) => {
  const { hasGetInitialPropsRun, error, msg, vctraceid } = props

  if (!hasGetInitialPropsRun && error) {
    // getInitialProps is not called in case of
    // https://github.com/vercel/next.js/issues/8592. As a workaround, we pass
    // err via _app.js so it can be captured
    Sentry.captureException(error, { extra: { vctraceid: vctraceid, msg: msg } })
  }

  if (error) {
    if (typeof window === 'undefined') {
      const logger = require('../../server/logger')

      logger.log({
        level: 'error',
        message: msg,
        error: error,
        vctraceid: vctraceid,
      })
    } else {
      console.error(error)
    }
  }

  return (
    <Container>
      <Helmet>
        <title>Something went wrong</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <HubImage src={require('./lighthouse.svg')} alt="Something went wrong" />
      <HubHeadline>
        <FormattedMessage {...messages.errorSomethingWentWrong} />
      </HubHeadline>
      <HubParagraph>{msg || <FormattedMessage {...messages.errorAnErrorOccured} />}</HubParagraph>
      <HubPre>
        <FormattedMessage {...messages.errorVctraceid} values={{ vctraceid }} />
      </HubPre>
      <HubParagraph>
        <FormattedMessage
          {...messages.errorTryAgainLater}
          values={{
            email: (
              <a href={`mailto:support@alida.com?subject=Hubs+error+${vctraceid}`}>
                <FormattedMessage {...messages.errorEmailTechnicalSupport} />
              </a>
            ),
          }}
        />
      </HubParagraph>
      {error && (process?.env?.POD === 'sit1' || process?.env?.POD?.startsWith('dev')) && (
        <HubPre>{error.stack ? JSON.stringify(error.stack) : JSON.stringify(error)}</HubPre>
      )}
    </Container>
  )
}

const Container = styled(RTLMainWrapper)`
  display: flex;
  height: 100vh;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  text-align: center;
  color: ${({ theme }) => theme.colors.grayDarkest};

  img {
    height: 50vh;
    min-height: 250px;
    max-height: 500px;
  }

  h1 {
    font-size: ${({ theme }) => theme.fontSizes.fontSizeLarge};
    font-weight: bold;
    margin-bottom: ${({ theme }) => theme.sizes.gutterLarge};
  }

  p {
    font-size: ${({ theme }) => theme.fontSizes.fontSizeSmall};
  }

  pre {
    font-size: ${({ theme }) => theme.fontSizes.fontSizeSmallest};
    text-align: left;
    max-width: 500px;
    max-height: 500px;
    overflow-y: auto;
    overflow-x: auto;
  }

  a {
    color: ${({ theme }) => theme.colors.primaryAccentColor};
  }
`

ErrorPage.getInitialProps = async ({
  req,
  res,
  error,
  asPath,
}: {
  req: Request
  res: Response
  error: Error
  asPath: string
}) => {
  // @ts-ignore
  const errorInitialProps: ErrorPageProps = await NextErrorComponent.getInitialProps(
    // @ts-ignore
    { res, error }
  )

  // This looks to be taken from:
  // https://leerob.io/blog/configuring-sentry-for-nextjs-apps

  // Workaround for https://github.com/vercel/next.js/issues/8592, mark when getInitialProps has run
  errorInitialProps.hasGetInitialPropsRun = true

  errorInitialProps.vctraceid = req.get('x-vctraceid') || ''

  // Running on the server, the response object (`res`) is available.
  //
  // Next.js will pass an err on the server if a page's data fetching methods
  // threw or returned a Promise that rejected
  //
  // Running on the client (browser), Next.js will provide an err if:
  //
  //  - a page's `getInitialProps` threw or returned a Promise that rejected
  //  - an exception was thrown somewhere in the React lifecycle (render,
  //    componentDidMount, etc) that was caught by Next.js's React Error
  //    Boundary. Read more about what types of exceptions are caught by Error
  //    Boundaries: https://reactjs.org/docs/error-boundaries.html

  if (res?.statusCode === 404) {
    // Opinionated: do not record an exception in Sentry for 404
    return { statusCode: 404 }
  }
  if (error) {
    Sentry.captureException(error)
    await Sentry.flush(2000)
    return errorInitialProps
  }

  // If this point is reached, getInitialProps was called without any
  // information about what the error might be. This is unexpected and may
  // indicate a bug introduced in Next.js, so record it in Sentry
  Sentry.captureException(
    // @ts-ignore
    new Error(`_error.js getInitialProps missing data at path: ${asPath}`)
  )
  await Sentry.flush(2000)

  return errorInitialProps
}

export default ErrorPage
